/* eslint-disable no-restricted-syntax */
import { propertyConverters } from '../converters/converter';
import { getDefinition, getTypedInheritanceChain } from '../classes/object-definition';
import { serdeGetType } from './compound-serde';
// import { getOriginalNamedOutput } from '../decorators/json-property';
function nullOrUndefined(value) {
    return value === null || value === undefined;
}
export function serialize(value, propertyDef, type, genericType) {
    // return null since undefined is not a valid JSON thing
    if (nullOrUndefined(value))
        return null;
    if (value.constructor === Array) {
        return value.map((o) => {
            if (nullOrUndefined(o))
                return null;
            return serializeRootObject(o, propertyDef, type, genericType);
        });
    }
    return serializeRootObject(value, propertyDef, type, genericType);
}
// doesn't check if it's array; where it's used we already know its not array
function isSimpleObject(object) {
    return typeof object === 'object' && object !== null;
}
function serializeRootObject(object, property, type = Object.getPrototypeOf(object).constructor, genericType) {
    const inheritanceChain = getTypedInheritanceChain(type);
    // if there's no inheritance chain, then it's just a simple json object, not an ELT object
    if (inheritanceChain.length === 0) {
        // check if its an object or not
        if (isSimpleObject(object)) {
            // iterate over children and try to serialize them
            const keyVals = Object.entries(object);
            // dates are objects, so handle it specially here by just returning it
            if (keyVals.length === 0)
                return object;
            // arrays are also objects, but just recursively call the main serialize function
            if (object.constructor === Array)
                return serialize(object, undefined);
            const serialized = {};
            for (const [key, value] of keyVals) {
                if (isSimpleObject(value)) {
                    serialized[key] = serialize(value, undefined);
                }
                else {
                    serialized[key] = value;
                }
            }
            return serialized;
        }
        return object;
    }
    const definitions = inheritanceChain.map((t) => getDefinition(t)); // getTypedInheritanceChain always returns object definitions
    const output = {};
    const additionals = definitions.filter((d) => d.additionalProperties);
    const keys = additionals.length === 0 ? undefined : new Set();
    for (const d of definitions) {
        for (const [key, p] of d.properties) {
            if (!p.type) {
                throw new Error(`Cannot serialize property '${key.toString()}' without type!`);
            }
            // object is the class instance object, so it will have the original name, not the serialized name (which is key)
            const value = p.hasJsonProperty ? object[p.originalName] : object[key];
            if (value === undefined || p.writeonly) {
                continue;
            }
            if (keys)
                keys.add(key);
            // Used when the field is Generic Type. Set the field's desired type
            if (genericType?.get(key)) {
                p.type = genericType.get(key);
            }
            if (p.set) {
                output[p.serializedName] = serializeArray(Array.from(value || []), p, genericType);
                continue;
            }
            if (p.array) {
                output[p.serializedName] = serializeArray(value, p, genericType);
                continue;
            }
            output[p.serializedName] = serializeObject(value, p, genericType);
        }
    }
    if (keys) {
        if (additionals.length > 1)
            throw new Error('Invalid schema: additionalProperties cannot be defined on both a child and its parent classes.');
        const additional = additionals[0].additionalProperties;
        if (!additional.type) {
            throw new Error(`Cannot serialize additionalProperties without their type!`);
        }
        for (const key in object) {
            if (keys.has(key))
                continue; // skip properties that were explicitly declared in schema
            const value = object[key];
            if (typeof value === 'function')
                continue; // do not serialize functions
            if (value === undefined || additional.writeonly) {
                continue;
            }
            if (additional.set) {
                output[key] = serializeArray(Array.from(value || []), additional, genericType);
                continue;
            }
            if (additional.array) {
                output[key] = serializeArray(value, additional, genericType);
                continue;
            }
            output[key] = serializeObject(value, additional, genericType);
        }
    }
    // validate sub and super classes that weren't already covered by the containing class, if any.
    definitions.forEach((d) => {
        if (d !== property && d.validate)
            d.validate(output);
    });
    return output;
}
function serializeArray(array, definition, genericType) {
    return array.map((v) => serializeObject(v, definition, genericType));
}
function serializeObject(object, definition, genericType) {
    const { typeToUse, isPrimitive } = serdeGetType(object, definition);
    const value = object;
    const converter = definition.converter || propertyConverters.get(typeToUse);
    try {
        if (converter) {
            return converter.serialize(value);
        }
    }
    catch (e) {
        // if we have something like
        //
        //	@JsonConverter(new DateConverter('date-time'))
        //	readonly value: Date | DateToolVariable;
        //
        // and the value is a dateToolVariable, we don't want to throw, and want to ignore this converter and continue
        // serdeGetType would have already picked out the proper type, DateToolVariable, already
        if (!definition.compoundType)
            throw e;
        console.info('Failed deserializing compound type with a converter, trying other non-primitive type');
    }
    if (!isPrimitive) {
        const objDefinition = getDefinition(typeToUse);
        if (objDefinition) {
            if (value instanceof typeToUse) {
                return serialize(value, objDefinition, undefined, genericType);
            }
            return serialize(value, objDefinition, typeToUse, genericType);
        }
    }
    return value;
}
//# sourceMappingURL=serialize.js.map